// A generation fence prevents an old account's async pairing from reaching the Watch.
export function createWatchCoordinator({ native, issue, revoke, capture, now = Date.now }) {
  let generation = 0, identity = null, eligible = false, busy = false, initialized = false;
  async function synchronize({ force = false } = {}) {
    if (busy || !eligible) return;
    const current = generation, account = identity;
    busy = true;
    try {
      const status = await native.status();
      if (current !== generation || !status.paired || !status.installed) return;
      if (status.accountID && status.accountID !== account) {
        await native.clear();
        if (current !== generation || !eligible) return;
      }
      if (!force && status.accountID === account && status.expiresAt * 1000 > now() + 3600000) return;
      const credential = await issue(status.installationId);
      if (current !== generation || credential.accountID !== identity || !eligible) { await revoke(credential.token); return; }
      await native.configure(credential);
      if (current !== generation) await native.clear();
    } finally {
      busy = false;
      if (current !== generation && eligible) void synchronize().catch(() => {});
    }
  }
  return {
    async update({ accountId, allowed, hydrated }) {
      if (!hydrated) return;
      const next = allowed && !!accountId;
      if (!initialized || identity !== accountId || eligible !== next) {
        const mustClear = !next || (initialized && identity !== accountId);
        initialized = true;
        generation++; identity = accountId; eligible = next;
        // Clear before replacing credentials, including account changes and revocation.
        if (mustClear) await native.clear();
      }
      if (eligible) await synchronize();
    },
    synchronize,
    async open() {
      const current = generation;
      const id = await native.consumeConversation();
      if (eligible && current === generation && /^[a-f0-9]{24}$/i.test(id || '')) capture(`/c/${id}`);
    },
    dispose() { generation++; eligible = false; },
  };
}
